import React, { useState, useEffect } from 'react';
import { Monitor, Smartphone, Tablet, Tv, Laptop, Loader2, Shield, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/api';
import { useViewerAuth } from '@/contexts/ViewerAuthContext';

const deviceIcons = {
  'iPhone': Smartphone,
  'iPad': Tablet,
  'Android Phone': Smartphone,
  'Android Tablet': Tablet,
  'Mac': Laptop,
  'Windows PC': Monitor,
  'Linux PC': Monitor,
  'Smart TV': Tv,
  'Roku': Tv,
  'Unknown Device': Monitor,
};

export const ManageDevicesPage = () => {
  const { viewer } = useViewerAuth();
  const [sessions, setSessions] = useState([]);
  const [maxDevices, setMaxDevices] = useState(2);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);

  const loadSessions = async () => {
    try {
      const res = await api.getViewerSessions();
      setSessions(res.data.sessions);
      setMaxDevices(res.data.max_devices);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (viewer) loadSessions();
  }, [viewer]);

  const handleRevoke = async (sessionId) => {
    setRevokingId(sessionId);
    try {
      await api.revokeSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch {
      alert('Failed to remove device.');
    } finally {
      setRevokingId(null);
    }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    const now = new Date();
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 1) return 'Active now';
    if (diffMin < 60) return `Active ${diffMin}m ago`;
    const diffHrs = Math.round(diffMin / 60);
    if (diffHrs < 24) return `Active ${diffHrs}h ago`;
    return `Active ${d.toLocaleDateString()}`;
  };

  if (!viewer) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <p className="text-gray-400">Please sign in to manage your devices.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-24 pb-12 px-4" data-testid="manage-devices-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-sky-500/10 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-sky-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Manage Devices</h1>
            <p className="text-gray-400 text-sm">
              {sessions.length} of {maxDevices} devices in use
            </p>
          </div>
        </div>

        {/* Usage Bar */}
        <div className="bg-gray-800 rounded-full h-2 mb-8 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              sessions.length >= maxDevices ? 'bg-red-500' : 'bg-sky-500'
            }`}
            style={{ width: `${(sessions.length / maxDevices) * 100}%` }}
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Monitor className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No active devices</p>
            <p className="text-sm mt-1">Start watching to see your devices here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map((session) => {
              const DeviceIcon = deviceIcons[session.device_name] || Monitor;
              return (
                <div
                  key={session.id}
                  className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-center justify-between hover:border-gray-700 transition-colors"
                  data-testid={`device-${session.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center">
                      <DeviceIcon className="w-6 h-6 text-sky-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{session.device_name}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(session.last_active)}
                        </span>
                        <span>{session.ip_address}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    onClick={() => handleRevoke(session.id)}
                    disabled={revokingId === session.id}
                    data-testid={`remove-device-${session.id}`}
                  >
                    {revokingId === session.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Remove'
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-8 p-4 bg-gray-900/50 rounded-xl border border-gray-800">
          <p className="text-gray-500 text-xs leading-relaxed">
            Your NLS+ subscription allows streaming on up to {maxDevices} devices at the same time. 
            Sessions automatically expire after 2 hours of inactivity. If you're having trouble streaming, 
            try removing an unused device above.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ManageDevicesPage;
